import { MeterView } from './MeterView';
import { VisitedView } from './VisitedView';
import { ViewData } from './ViewData';
import { ViewsRepository } from '@Engage.Data/repositories/views/ViewsRepository';
import { AppOptions } from '../../../application/options/AppOptions';
import { CookiesStorageService } from '@Engage.Data.Services';
import { localStorageService } from '@Engage.Data/services/storage/localStorage/LocalStorageService';
import { StorageKeys, MeteredArticleCountObj } from '@Engage.Data.Constants';
import { customTime, logService } from '@Engage.Core';
import MD5 from 'crypto-js/md5';
import { UserStore } from '../../../plugins/auth/model/UserStore';
class ViewClass {
    constructor() {
        this.meterViews = [];
        this.isHaveRequestForSaveView = false;
    }
    getPageViewCount() {
        var _a;
        return parseInt((_a = localStorageService.get('nxt_page_view_count', { expandKey: false })) !== null && _a !== void 0 ? _a : '0', 10) || 0;
    }
    incrementPageViewCount() {
        const count = this.getPageViewCount() + 1;
        localStorageService.set('nxt_page_view_count', String(count), { expandKey: false });
    }
    // ─── Initialisation ───────────────────────────────────────────────────────
    async setViewData() {
        this.viewData = new ViewData();
        this.visitedView = new VisitedView();
        try {
            logService.log('[View] Loading meter views');
            this.meterViews = await this.viewData.createViews();
            this.setViewStructureIfEmpty();
            this.setUserSessionStorageIfEmpty();
            logService.log('[View] Meter views loaded', `count=${this.meterViews.length}`);
            return true;
        }
        catch (error) {
            logService.error('[View] Cannot create Views', error);
            throw error;
        }
    }
    // ─── Active meter ─────────────────────────────────────────────────────────
    setActiveMeterView(meterLevel) {
        var _a;
        this.activeMeterView = this.meterViews.find((mv) => String(mv.meterLevel) === String(meterLevel));
        if (!this.activeMeterView) {
            // Try to find an existing meter view with a different key format before creating empty
            // This handles cases where cookie key and paywall response key differ slightly
            this.activeMeterView = (_a = this.meterViews.find(mv => mv.meterLevel !== -1)) !== null && _a !== void 0 ? _a : null;
            if (!this.activeMeterView) {
                logService.warn('[View] No meter view found, creating empty', `meterLevel=${meterLevel}`);
                this.activeMeterView = new MeterView(meterLevel, {});
                this.meterViews.push(this.activeMeterView);
            }
            else {
                logService.warn('[View] Meter level key mismatch, using existing meter view', `requested=${meterLevel}`, `found=${this.activeMeterView.meterLevel}`);
                // Update the key to match what the server returned
                this.activeMeterView.meterLevel = meterLevel;
            }
        }
        else {
            logService.log('[View] Active meter set', `meterLevel=${meterLevel}`);
        }
    }
    // ─── Conversation ─────────────────────────────────────────────────────────
    setCurrentConversation(conversationCode, conversationId) {
        if (this.activeMeterView) {
            logService.log('[View] Setting conversation', `code=${conversationCode}`, `id=${conversationId}`);
            this.activeMeterView.setActiveConversation(conversationCode, conversationId);
        }
        else {
            logService.error('[View] Active meter is not set — cannot set conversation');
        }
        // Persist to localStorage (mirrors ConneXt v2 CampaignRepository.setCurrentConversation)
        if (conversationCode && conversationCode !== 'null') {
            localStorageService.set(StorageKeys.CurrentConversation, {
                Id: conversationId,
                Code: conversationCode
            }, { expandKey: false });
        }
    }
    incrementConversationCount(conversationCode, conversationId) {
        this.incrementPageViewCount();
        this.setUpdateArticleCountCookie();
        this.setCurrentConversation(conversationCode, conversationId);
        this.updateViewedArticles(conversationCode);
    }
    getCurrentConversationViewCount() {
        return this.activeMeterView ? this.activeMeterView.getCurrentConversationViewCount() : 0;
    }
    setCurrentConversationViewCount(viewCount) {
        var _a;
        (_a = this.activeMeterView) === null || _a === void 0 ? void 0 : _a.setCurrentConversationViewCount(viewCount);
    }
    getActiveConversationCode() {
        if (this.activeMeterView) {
            return this.activeMeterView.activeConversationCode;
        }
        // Pre-response: scan loaded meterViews for active conversation code
        for (const mv of this.meterViews) {
            if (mv.activeConversationCode && mv.activeConversationCode !== 'null') {
                return mv.activeConversationCode;
            }
        }
        return null;
    }
    getActiveConversationId() {
        var _a;
        // If activeMeterView is set, use it
        if (((_a = this.activeMeterView) === null || _a === void 0 ? void 0 : _a.activeConversationId) > 0) {
            return this.activeMeterView.activeConversationId;
        }
        // Pre-response: scan loaded meterViews for active conversation ID
        for (const mv of this.meterViews) {
            if (mv.activeConversationId > 0) {
                return mv.activeConversationId;
            }
        }
        // Fallback: read from localStorage (mirrors ConneXt v2 CampaignRepository.getCurrentConversation())
        const stored = localStorageService.get(StorageKeys.CurrentConversation, { expandKey: false });
        if (stored && stored.Id > 0) {
            return stored.Id;
        }
        return null;
    }
    getConversationStartDate() {
        // If activeMeterView is set, use it (normal post-response flow)
        if (this.activeMeterView) {
            return this.activeMeterView.getConversationStartDate();
        }
        // Pre-response: scan loaded meterViews for the active conversation's start date
        // (mirrors ConneXt v2 which reads from stored conversation before paywall call)
        for (const mv of this.meterViews) {
            if (mv.activeConversationCode && mv.activeConversationCode !== 'null') {
                const cv = mv.conversationsViews.find(c => c.code === mv.activeConversationCode);
                if (cv && cv.getStartDate()) {
                    return cv.getStartDate();
                }
            }
        }
        return null;
    }
    // ─── Article tracking ─────────────────────────────────────────────────────
    getCurrentDynamicMeterViewCount() {
        return this.activeMeterView ? this.activeMeterView.articleCount : 0;
    }
    // Returns total article count across ALL meter views — available after setViewData(),
    // before setActiveMeterView(). Used by qualifiers that run before meter is resolved.
    getTotalArticleCount() {
        var _a;
        if (!((_a = this.meterViews) === null || _a === void 0 ? void 0 : _a.length))
            return 0;
        return this.meterViews.reduce((sum, mv) => sum + (mv.articleCount || 0), 0);
    }
    // Returns the active conversation view count across all meter views.
    // Finds the conversation matching activeConversationCode in each meter view.
    getTotalConversationViewCount() {
        var _a;
        if (!((_a = this.meterViews) === null || _a === void 0 ? void 0 : _a.length))
            return 0;
        let total = 0;
        for (const mv of this.meterViews) {
            if (!mv.activeConversationCode || mv.activeConversationCode === 'null')
                continue;
            const cv = mv.conversationsViews.find(c => c.code === mv.activeConversationCode);
            if (cv)
                total += cv.viewCount;
        }
        return total;
    }
    setCurrentDynamicMeterViewCount(viewCount) {
        if (this.activeMeterView)
            this.activeMeterView.articleCount = viewCount;
    }
    getViewedArticles(conversationCode) {
        var _a;
        const viewsObj = (_a = this.viewData) === null || _a === void 0 ? void 0 : _a.getViewedArticles();
        return viewsObj ? viewsObj[conversationCode] : null;
    }
    getUniqueArticles() {
        return localStorageService.get(StorageKeys.UniqueArticles, { expandKey: false });
    }
    setUniqueArticles(count) {
        localStorageService.set(StorageKeys.UniqueArticles, count, { expandKey: false });
    }
    isCurrentArticleNew() {
        return localStorageService.get(StorageKeys.isCurrentArticleNew, { expandKey: false });
    }
    // ─── Free articles ────────────────────────────────────────────────────────
    updateFreeArticleRemaining() {
        var _a;
        (_a = this.activeMeterView) === null || _a === void 0 ? void 0 : _a.reduceArticleCount();
    }
    isFreeArticleAvailable() {
        return this.activeMeterView ? this.activeMeterView.isFreeArticleAvailable() : 0;
    }
    isFreePreviousArticle() {
        const list = localStorageService.get(StorageKeys.ConnextFAL, { expandKey: false });
        const articleId = View.getArticleId();
        return list && list.includes(articleId) ? 1 : 0;
    }
    // ─── Visited views ────────────────────────────────────────────────────────
    getVisitedViewsByDays(days) {
        var _a, _b;
        const count = (_b = (_a = this.visitedView) === null || _a === void 0 ? void 0 : _a.getVisitedViewsByDays(days)) !== null && _b !== void 0 ? _b : 0;
        logService.log('[View] getVisitedViewsByDays', `days=${days}`, `count=${count}`, `visitedViewReady=${!!this.visitedView}`);
        return count;
    }
    // ─── Save / send ──────────────────────────────────────────────────────────
    saveViewInformation() {
        const conversationViewCount = this.getCurrentConversationViewCount();
        const dynamicMeterViewCount = this.getCurrentDynamicMeterViewCount();
        this.saveViewStructure();
        const batchCount = 3;
        // Mirrors ConneXt v2: no POST on view 1, POST on view 3, 6, 9...
        // conversationViewCount > 1 ensures first view is skipped
        const needSave = (conversationViewCount > 1 && conversationViewCount % batchCount === 0)
            || (dynamicMeterViewCount > 1 && dynamicMeterViewCount % batchCount === 0);
        logService.log('[View] saveViewInformation', `conversationViewCount=${conversationViewCount}`, `dynamicMeterViewCount=${dynamicMeterViewCount}`, `willPost=${needSave || this.isHaveRequestForSaveView}`);
        if (needSave || this.isHaveRequestForSaveView) {
            return this.sendViewData();
        }
    }
    async sendViewData() {
        var _a, _b;
        logService.log('[View] POST views — sending view data');
        this.isHaveRequestForSaveView = false;
        const sendData = await this.buildSendViewData();
        const data = await ViewsRepository.postViews(sendData);
        if ((data === null || data === void 0 ? void 0 : data.LastResetDate) !== undefined)
            (_a = this.viewData) === null || _a === void 0 ? void 0 : _a.saveLastResetDate(data.LastResetDate);
        if (data === null || data === void 0 ? void 0 : data.UserStorageSession)
            (_b = this.viewData) === null || _b === void 0 ? void 0 : _b.saveUserSessions(data.UserStorageSession);
        logService.log('[View] POST views — complete');
    }
    setRequestForSaveView(newConversationCode, oldConversationCode) {
        if (newConversationCode && oldConversationCode && newConversationCode !== oldConversationCode) {
            this.isHaveRequestForSaveView = true;
        }
    }
    // ─── Reset ────────────────────────────────────────────────────────────────
    resetViews() {
        this.meterViews = [];
        this.sendViewData();
    }
    resetStorage() {
        this.meterViews = [];
        if (this.viewData) {
            this.viewData.deleteViewedArticles();
        }
        // Clear persisted page view count so batch trigger resets correctly
        localStorageService.set('nxt_page_view_count', '0', { expandKey: false });
        CookiesStorageService.delete(StorageKeys.ConnextViewStructure, {
            useRootDomain: false
        });
    }
    resetArticleViews() {
        var _a, _b;
        const storedViews = ((_a = this.viewData) === null || _a === void 0 ? void 0 : _a.getViewStructure()) || {};
        this.resetStorage();
        for (const meterLevelId in storedViews) {
            const preserved = {};
            preserved[MeteredArticleCountObj.ActiveConvoCode] = storedViews[meterLevelId][MeteredArticleCountObj.ActiveConvoCode];
            storedViews[meterLevelId] = preserved;
        }
        (_b = this.viewData) === null || _b === void 0 ? void 0 : _b.setViewStructure(storedViews);
    }
    resetConversationView(conversationCode) {
        var _a;
        (_a = this.activeMeterView) === null || _a === void 0 ? void 0 : _a.resetConversationView(conversationCode);
        if (this.viewData) {
            const viewsObj = this.viewData.getViewedArticles() || {};
            viewsObj[conversationCode] = [];
            this.viewData.setViewedArticles(viewsObj);
        }
    }
    resetMeterLevel() {
        var _a;
        (_a = this.activeMeterView) === null || _a === void 0 ? void 0 : _a.reset();
    }
    resetFreeArticle() {
        var _a, _b;
        const storedViews = ((_a = this.viewData) === null || _a === void 0 ? void 0 : _a.getViewStructure()) || {};
        for (const meterLevelId in storedViews) {
            for (const key in storedViews[meterLevelId]) {
                const entry = storedViews[meterLevelId][key];
                if (entry && entry['far'] !== undefined) {
                    entry['far'] = 0;
                    entry['frp'] = [];
                }
            }
        }
        (_b = this.viewData) === null || _b === void 0 ? void 0 : _b.setViewStructure(storedViews);
    }
    // ─── Sync ─────────────────────────────────────────────────────────────────
    async syncViewData() {
        try {
            logService.log('[View] syncViewData — syncing with server');
            const sendData = await this.buildSendViewData();
            const successData = await ViewsRepository.postViews(sendData);
            logService.log('[View] syncViewData — complete');
            return this.viewData.createViewsFromApi(successData);
        }
        catch (error) {
            logService.error('[View] syncViewData error', error);
            return [];
        }
    }
    // ─── Article ID ───────────────────────────────────────────────────────────
    getArticleId() {
        return MD5(window.location.href).toString();
    }
    // ─── Private helpers ──────────────────────────────────────────────────────
    async buildSendViewData() {
        var _a;
        const viewData = await this.getLocalViewData();
        const data = {
            UserId: this.getUserId(),
            ConfigCode: AppOptions.getConfigCode(),
            SiteCode: AppOptions.getSiteCode(),
            SettingsKey: (_a = AppOptions.getSettingsKey()) !== null && _a !== void 0 ? _a : null,
            ViewData: viewData
        };
        const masterId = this.getMasterId();
        if (masterId)
            data['masterId'] = masterId;
        return data;
    }
    getLocalViewData() {
        var _a, _b, _c, _d;
        try {
            const meterLevelsViewData = this.meterViews
                .filter(m => m.meterLevel !== -1)
                .reduce((acc, m) => Object.assign(acc, m.getViewData()), {});
            return {
                CodelistSet: null,
                DynamicMeterViews: meterLevelsViewData,
                LastResetDate: (_b = (_a = this.viewData) === null || _a === void 0 ? void 0 : _a.getLastResetDate()) !== null && _b !== void 0 ? _b : '',
                UserStorageSession: (_d = (_c = this.viewData) === null || _c === void 0 ? void 0 : _c.getUserSessions()) !== null && _d !== void 0 ? _d : []
            };
        }
        catch (ex) {
            logService.error('[View] getLocalViewData error', ex);
            return {};
        }
    }
    updateViewedArticles(conversationCode) {
        var _a, _b, _c, _d;
        try {
            localStorageService.delete(StorageKeys.isCurrentArticleNew, { expandKey: false });
            const viewedArticles = this.getViewedArticles(conversationCode) || [];
            const articleId = this.getArticleId();
            const uniqueArticlesMode = !!this.getUniqueArticles();
            if (viewedArticles.indexOf(articleId) > -1) {
                // Article already viewed — only increment if NOT in unique articles mode
                // (mirrors ConneXt v2: increment always unless uniqueArticles debug mode is on)
                if (!uniqueArticlesMode) {
                    logService.log('[View] Article already viewed — incrementing', `articleId=${articleId}`);
                    (_a = this.activeMeterView) === null || _a === void 0 ? void 0 : _a.increment();
                }
                else {
                    logService.log('[View] Article already viewed — skipping increment (uniqueArticles mode)', `articleId=${articleId}`);
                }
            }
            else {
                logService.log('[View] New article view', `articleId=${articleId}`, `conversationCode=${conversationCode}`);
                viewedArticles.push(articleId);
                (_b = this.activeMeterView) === null || _b === void 0 ? void 0 : _b.increment();
                const viewsObj = ((_c = this.viewData) === null || _c === void 0 ? void 0 : _c.getViewedArticles()) || {};
                viewsObj[conversationCode] = viewedArticles;
                (_d = this.viewData) === null || _d === void 0 ? void 0 : _d.setViewedArticles(viewsObj);
                localStorageService.set(StorageKeys.isCurrentArticleNew, true, { expandKey: false });
            }
        }
        catch (ex) {
            logService.error('[View] updateViewedArticles error', ex);
        }
    }
    setViewStructureIfEmpty() {
        var _a;
        const localStoredViews = (_a = this.viewData) === null || _a === void 0 ? void 0 : _a.getViewStructure();
        if (!localStoredViews ||
            (Array.isArray(localStoredViews) && localStoredViews.length === 0) ||
            (typeof localStoredViews === 'object' && Object.keys(localStoredViews).length === 0)) {
            this.saveViewStructure();
        }
    }
    saveViewStructure() {
        var _a;
        const cookie = {};
        this.meterViews.forEach(mv => { cookie[mv.meterLevel] = mv.renderCookieObject(); });
        (_a = this.viewData) === null || _a === void 0 ? void 0 : _a.setViewStructure(cookie);
    }
    setUpdateArticleCountCookie() {
        const expire = customTime.get();
        expire.setHours(expire.getHours() + 24);
        CookiesStorageService.set(StorageKeys.ConnextUpdateArticleCount, '1', {
            expandKey: false,
            expires: expire
        });
    }
    getUserId() {
        var _a;
        return (_a = CookiesStorageService.get(StorageKeys.ConnextUserId, {
            expandKey: false,
            isSecureValue: false,
            useRootDomain: true
        })) !== null && _a !== void 0 ? _a : null;
    }
    getMasterId() {
        var _a, _b;
        try {
            return (_b = (_a = UserStore.getUser()) === null || _a === void 0 ? void 0 : _a.MasterId) !== null && _b !== void 0 ? _b : null;
        }
        catch (_c) {
            return null;
        }
    }
    // ─── User session storage (mirrors ConneXt v2) ────────────────────────────
    generateUserSession() {
        var _a;
        return {
            BrowserSessionId: (_a = AppOptions.getBrowserSessionId()) !== null && _a !== void 0 ? _a : '',
            PageSessionId: AppOptions.getPageSessionId(),
            StartDateTime: customTime.get(),
            EndDateTime: null
        };
    }
    setUserSessionStorageIfEmpty() {
        var _a, _b, _c;
        const MAX_SESSIONS = 10;
        const EXPIRE_DAYS = 30;
        let existing = (_b = (_a = this.viewData) === null || _a === void 0 ? void 0 : _a.getUserSessions()) !== null && _b !== void 0 ? _b : [];
        const currentSession = this.generateUserSession();
        // append current session
        existing.push(currentSession);
        // purge sessions older than EXPIRE_DAYS
        const expireDate = new Date(customTime.get().getTime() - EXPIRE_DAYS * 24 * 60 * 60 * 1000);
        existing = existing.filter(s => new Date(s.StartDateTime) > expireDate);
        // keep only the last MAX_SESSIONS entries
        if (existing.length > MAX_SESSIONS) {
            existing = existing.slice(existing.length - MAX_SESSIONS);
        }
        (_c = this.viewData) === null || _c === void 0 ? void 0 : _c.saveUserSessions(existing);
    }
}
export const View = new ViewClass();
